import React, { useEffect, useState } from "react";
import {
  makeStyles,
  Theme,
  createStyles,
  Paper,
  Grid,
  TextField,
  withStyles,
  Box,
  Icon,
} from "@material-ui/core";
import {
  VisibilityOutlined,
  VisibilityOffOutlined,
} from "@material-ui/icons";
import { Link as NavigateLink, useNavigate } from "react-router-dom";
import { ReactComponent as Logo } from "../../img/ic4-logo.svg";
import HttpClient, { HttpMutationOutput } from "../../lib/HttpClient";
import {
  setAuthProfile,
  setAuthToken,
  getAuthToken,
  clearPortalAuth,
} from "../../lib/authStorage";
import { getPortalAuthConfig } from "../../portals/portalConfig";
import PortalProfileLoadResponse from "../../models/serviceApi/PortalProfileLoadResponse";
import { useSnackbarContext } from "../../contexts/Snackbar";
import {
  clearCaPasswordResetSession,
  getCaPasswordResetSession,
} from "../../lib/caPasswordResetSession";

const MIN_PASSWORD_LENGTH = 8;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 40,
    },
    paper: {
      backgroundColor: "#0E426A",
      alignItems: "center",
      justifyContent: "center",
      padding: 20,
      minHeight: 480,
      width: 550,
    },
    title: {
      fontSize: 26,
      color: "#ffffff",
    },
    subtitle: {
      fontSize: 13,
      color: "#ffffff",
      opacity: 0.9,
      textAlign: "center",
      maxWidth: 400,
      marginTop: 8,
    },
    label: {
      fontSize: 12,
      color: "#ffffff",
      padding: 10,
    },
    input: {
      width: 300,
      height: 30,
      margin: 0,
    },
    inputBox: {
      backgroundColor: "#FFFFFF",
      height: 45,
      width: 300,
      paddingTop: 5,
      borderRadius: 5,
    },
    linkLarge: {
      color: "#F78F2D",
      fontSize: 14,
    },
  }),
);

const CustomTextField = withStyles({
  root: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "grey",
      },
    },
  },
})(TextField);

export default function CaChangePasswordPage() {
  const classes = useStyles();
  const navigate = useNavigate();
  const { setSnackbar } = useSnackbarContext();
  const authConfig = getPortalAuthConfig();
  const [resetSession] = useState(() => getCaPasswordResetSession());
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!getAuthToken() || !resetSession) {
      navigate("/");
    }
  }, [navigate, resetSession]);

  if (!resetSession) {
    return null;
  }

  const passwordsMatch = password === confirmPassword;
  const isPasswordValid = password.length >= MIN_PASSWORD_LENGTH;
  const canSubmit =
    isPasswordValid && passwordsMatch && confirmPassword.length > 0 && !isLoading;

  async function loadProfileAfterReset() {
    const { body } = await HttpClient.performGet<PortalProfileLoadResponse>(
      authConfig.profileUrl,
    );
    if (!body?.status?.success) {
      throw new Error(body?.status?.message ?? "Could not load profile");
    }
    setAuthProfile(body.profile);
    navigate(authConfig.afterLoginPath);
  }

  async function handleSubmit() {
    const session = getCaPasswordResetSession();
    if (!canSubmit || !session) {
      return;
    }

    setIsLoading(true);
    try {
      const response: HttpMutationOutput<{
        status?: { success: boolean; message?: string };
        token?: string;
      }> = await HttpClient.performPost(authConfig.setPasswordUrl, {
        email: session.email,
        password,
        ...(session.tid ? { tid: session.tid } : {}),
      });

      if (!response.body?.status?.success) {
        setSnackbar({
          message:
            response.body?.status?.message ??
            "Failed to set password. Please try again.",
          severity: "error",
        });
        return;
      }

      clearCaPasswordResetSession();

      if (response.body.token) {
        setAuthToken(response.body.token);
        setSnackbar({
          message: "Password updated successfully.",
          severity: "success",
        });
        await loadProfileAfterReset();
      } else {
        clearPortalAuth();
        setSnackbar({
          message: "Password updated. Please sign in with your new password.",
          severity: "success",
        });
        navigate("/");
      }
    } catch (e) {
      setSnackbar({
        message:
          e instanceof Error ? e.message : "Failed to set password.",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Grid
      container
      direction="column"
      alignItems="center"
      justifyContent="center"
      spacing={5}
      className={classes.root}
    >
      <Grid item>
        <Logo />
      </Grid>
      <Grid item>
        <Paper className={classes.paper} elevation={0}>
          <Grid container direction="column" alignItems="center" spacing={2}>
            <Grid item>
              <label className={classes.title}>Set a new password</label>
              <p className={classes.subtitle}>
                For your security, please choose a new password before
                continuing.
              </p>
            </Grid>

            <Grid item>
              <label className={classes.label}>New password</label>
              <Box className={classes.inputBox}>
                <CustomTextField
                  className={classes.input}
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  variant="outlined"
                  size="small"
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <Icon
                        onClick={() => setShowPassword((v) => !v)}
                        style={{ cursor: "pointer", marginRight: 8 }}
                      >
                        {showPassword ? (
                          <VisibilityOutlined />
                        ) : (
                          <VisibilityOffOutlined />
                        )}
                      </Icon>
                    ),
                  }}
                />
              </Box>
              {password.length > 0 && password.length < MIN_PASSWORD_LENGTH && (
                <span className={classes.subtitle}>
                  At least {MIN_PASSWORD_LENGTH} characters required.
                </span>
              )}
            </Grid>

            <Grid item>
              <label className={classes.label}>Confirm new password</label>
              <Box className={classes.inputBox}>
                <CustomTextField
                  className={classes.input}
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  variant="outlined"
                  size="small"
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <Icon
                        onClick={() => setShowConfirm((v) => !v)}
                        style={{ cursor: "pointer", marginRight: 8 }}
                      >
                        {showConfirm ? (
                          <VisibilityOutlined />
                        ) : (
                          <VisibilityOffOutlined />
                        )}
                      </Icon>
                    ),
                  }}
                />
              </Box>
              {confirmPassword.length > 0 && !passwordsMatch && (
                <span className={classes.subtitle}>Passwords do not match.</span>
              )}
            </Grid>

            <Grid item>
              <NavigateLink
                to="/"
                className={classes.linkLarge}
                onClick={() => {
                  clearCaPasswordResetSession();
                  clearPortalAuth();
                }}
              >
                Back to Login
              </NavigateLink>
            </Grid>

            <Grid item>
              <button
                className={
                  canSubmit ? "primary_button" : "primary_button_disabled"
                }
                disabled={!canSubmit}
                onClick={handleSubmit}
              >
                {isLoading ? "Saving..." : "Save password"}
              </button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
}
